import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, InputLabel,
  MenuItem, Select, Stack, TextField,
} from '@mui/material';
import * as api from '../plannerApi';
import * as T from '../types';

/** A quantity as typed in a field: "1,5" (no grouping). */
export const toInput = (value?: number | null) => (value === null || value === undefined ? '' : String(value).replace('.', ','));

/** Asks for one line of text; `onSubmit` returns an error to keep it open, or nothing to close. */
export function PromptDialog({ open, title, text, label, placeholder, initial = '', confirmText = 'OK', onClose, onSubmit }: {
  open: boolean; title: string; text?: string; label?: string; placeholder?: string; initial?: string; confirmText?: string;
  onClose: () => void; onSubmit: (value: string) => Promise<string | void>;
}) {
  const [value, setValue] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setValue(initial); setError(null); setBusy(false); }
  }, [open, initial]);

  const submit = async () => {
    if (!value.trim()) return;
    setBusy(true);
    const result = await onSubmit(value.trim());
    setBusy(false);
    if (result) setError(result); else onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={e => { e.preventDefault(); submit(); }}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {text && <DialogContentText sx={{ mb: 1 }}>{text}</DialogContentText>}
          <TextField
            autoFocus fullWidth margin="dense" label={label} placeholder={placeholder} value={value}
            onChange={e => setValue(e.target.value)} slotProps={{ htmlInput: { maxLength: 60 } }}
          />
          {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={busy || !value.trim()}>{confirmText}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

const OWN_PLACE = '__place';

/** A new place, or a zone inside one ("Armário de cima" in "Despensa"); returns it through `onCreated`. */
export function NewLocationDialog({ open, locations, onClose, onCreated }: {
  open: boolean; locations: T.StorageLocation[]; onClose: () => void; onCreated: (location: T.StorageLocation) => void;
}) {
  const [name, setName] = useState('');
  const [parent, setParent] = useState(OWN_PLACE);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const places = locations.filter(l => !l.parentPublicId);

  useEffect(() => {
    if (open) { setName(''); setParent(OWN_PLACE); setError(null); setBusy(false); }
  }, [open]);

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const location = await api.createStorageLocation(name.trim(), parent === OWN_PLACE ? null : parent);
      onCreated(location);
    } catch (e: any) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={e => { e.preventDefault(); create(); }}>
        <DialogTitle>Novo sítio</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              autoFocus label="Nome" placeholder="Ex.: Armário de cima, Arrecadação" value={name}
              onChange={e => setName(e.target.value)} slotProps={{ htmlInput: { maxLength: 60 } }}
            />
            <FormControl>
              <InputLabel id="parent-label">Onde fica?</InputLabel>
              <Select labelId="parent-label" label="Onde fica?" value={parent} onChange={e => setParent(e.target.value)}>
                <MenuItem value={OWN_PLACE}>É um local (não fica dentro de outro)</MenuItem>
                {places.map(p => <MenuItem key={p.publicId} value={p.publicId}>Dentro de: {p.name}</MenuItem>)}
              </Select>
            </FormControl>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={busy || !name.trim()}>Juntar</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
